<template>
  <BittsCard
    title-text="Potential Revenue Settings"
    class="potential-revenue-card"
  >
    <div class="p-16 pt-0 flex-col items-stretch">
      <div v-if="!hasDealSources" class="mt-20 mb-16">
        <BittsCallout
          v-if="!calloutDismissed"
          size="x-small"
          type="info"
          title="Add a CRM data source to gain access to revenue metrics"
          subtitle="We need access to opportunity data to be able to generate these numbers"
          action-text="Connect Data Source"
          :dismiss="true"
          @bitts-callout-action="handleConnectDataSource"
          @bitts-callout-dismiss="handleDismiss"
        />
      </div>
      <h3>Potential Revenue Populations</h3>
      <div class="potential-revenue-card__text">
        <p
          >For each partnership, we calculate a potential revenue number based
          on Open Opportunities in your Populations.</p
        >
        <p>
          Below you can customize which Populations are used in this
          calculation.
          <BittsLink
            url="https://help.crossbeam.com/en/articles/6797349-potential-revenue"
            text="Learn more about this feature"
          />
        </p>
      </div>
      <div class="potential-revenue-card__settings">
        <div class="w-full">
          <BittsCheckboxGroup
            form-label="Your Populations"
            :class="{ 'cursor-not-allowed': !hasOrgSettingsPermission }"
            class="potential-revenue-card__checkbox-group"
          >
            <BittsCheckbox
              v-for="setting in ownRevenueSettings"
              :key="setting.title"
              :checked="setting.isChecked"
              :disabled="!hasOrgSettingsPermission || !hasDealSourcesAndFields"
              :label="setting.title"
              @input="onSettingSelected(setting.type, ORG_TYPE.OWN)"
            />
          </BittsCheckboxGroup>
        </div>
        <FontAwesomeIcon
          :icon="['fak', 'mapping']"
          :style="{ width: '16px', height: '16px', color: 'currentColor' }"
          class="text-neutral-accent px-12"
        />
        <div class="w-full">
          <BittsCheckboxGroup
            form-label="Partner Populations"
            :class="{ 'cursor-not-allowed': !hasOrgSettingsPermission }"
            class="potential-revenue-card__checkbox-group"
          >
            <BittsCheckbox
              v-for="setting in partnerRevenueSettings"
              :key="setting.title"
              :checked="setting.isChecked"
              :disabled="!hasOrgSettingsPermission || !hasDealSourcesAndFields"
              :label="setting.title"
              @input="onSettingSelected(setting.type, ORG_TYPE.PARTNER)"
            />
          </BittsCheckboxGroup>
        </div>
      </div>
      <BittsButton
        v-if="hasOrgSettingsPermission"
        :disabled="!haveSettingsChanged"
        :loading="isSaving"
        size="small"
        text="Save Settings"
        @click="onSaveSettings"
      />
    </div>
  </BittsCard>
</template>

<script setup lang="ts">
import {
  BittsButton,
  BittsCallout,
  BittsCard,
  BittsCheckbox,
  BittsCheckboxGroup,
  BittsLink,
} from '@crossbeam/bitts';
import { EVENT_SITES } from '@crossbeam/itly';

import { computed, onMounted, reactive, ref } from 'vue';
import { useRouter } from 'vue-router';

import { crossbeamApi } from '@/api';
import useAuth from '@/composables/useAuth';
import useIteratively from '@/composables/useIteratively';
import { usePreferredCRMFeed } from '@/composables/usePreferredCRMFeed';
import { captureException } from '@/errors';
import {
  useFeedsStore,
  useFlashesStore,
  usePotentialRevenueStore,
  useSourcesStore,
} from '@/stores';
import { PotentialRevenueSettings } from '@/types/potential_revenue';

defineEmits(['close-and-redirect']);

const ORG_TYPE = {
  OWN: 'our_populations_potential_influence',
  PARTNER: 'partner_populations_potential_influence',
} as const;
type OrgType = (typeof ORG_TYPE)[keyof typeof ORG_TYPE];

const POPULATION_TYPE = {
  CUSTOMERS: 'customers',
  OPEN_OPPS: 'open_opportunities',
  PROSPECTS: 'prospects',
  CUSTOM: 'custom',
} as const;
type PopulationType = (typeof POPULATION_TYPE)[keyof typeof POPULATION_TYPE];

const { hasScope } = useAuth();
const { iteratively } = useIteratively();
const { addSuccessFlash, addErrorFlash } = useFlashesStore();
const potRevSettingsStore = usePotentialRevenueStore();
const feedsStore = useFeedsStore();
const sourcesStore = useSourcesStore();
const router = useRouter();

const isSaving = ref<boolean>(false);

const haveSettingsChanged = ref<boolean>(false);
const calloutDismissed = ref<boolean>(false);
const potentialRevenueSettings = reactive<PotentialRevenueSettings>({});
const hasOrgSettingsPermission = computed(() => hasScope('write:organization'));

const ownRevenueSettings = computed(() => {
  return [
    {
      title: 'Your Customers',
      isChecked: isChecked(
        POPULATION_TYPE.CUSTOMERS as PopulationType,
        ORG_TYPE.OWN as OrgType,
      ),
      type: POPULATION_TYPE.CUSTOMERS as PopulationType,
    },

    {
      title: 'Your Open Opportunities',
      isChecked: isChecked(POPULATION_TYPE.OPEN_OPPS, ORG_TYPE.OWN),
      type: POPULATION_TYPE.OPEN_OPPS,
    },
    {
      title: 'Your Prospects',
      isChecked: isChecked(POPULATION_TYPE.PROSPECTS, ORG_TYPE.OWN),
      type: POPULATION_TYPE.PROSPECTS,
    },
    {
      title: 'All Custom Populations',
      isChecked: isChecked(POPULATION_TYPE.CUSTOM, ORG_TYPE.OWN),
      type: POPULATION_TYPE.CUSTOM,
    },
  ];
});

const partnerRevenueSettings = computed(() => {
  return [
    {
      title: 'Partner Customers',
      isChecked: isChecked(POPULATION_TYPE.CUSTOMERS, ORG_TYPE.PARTNER),
      type: POPULATION_TYPE.CUSTOMERS,
    },
    {
      title: 'Partner Open Opportunities',
      isChecked: isChecked(POPULATION_TYPE.OPEN_OPPS, ORG_TYPE.PARTNER),
      type: POPULATION_TYPE.OPEN_OPPS,
    },
    {
      title: 'Partner Prospects',
      isChecked: isChecked(POPULATION_TYPE.PROSPECTS, ORG_TYPE.PARTNER),
      type: POPULATION_TYPE.PROSPECTS,
    },
    {
      title: 'All Custom Populations',
      isChecked: isChecked(POPULATION_TYPE.CUSTOM, ORG_TYPE.PARTNER),
      type: POPULATION_TYPE.CUSTOM,
    },
  ];
});

const hasDealSources = computed(() => {
  return sourcesStore.dealSources.length > 0;
});

const { preferredCRMFeed } = usePreferredCRMFeed();

const hasDealSourcesAndFields = computed(() => {
  const feedId = preferredCRMFeed.value?.id;
  if (!hasDealSources.value || !feedId) return false;

  return sourcesStore.dealSources
    .filter((source) => source?.feed_id === feedId)
    .flatMap((source) => source.fields)
    .some(
      (f) =>
        f.is_visible &&
        (f.pg_data_type === 'double precision' || f.pg_data_type === 'money'),
    );
});

onMounted(async () => {
  await Promise.all([
    potRevSettingsStore.readySync,
    feedsStore.readySync,
    sourcesStore.readySync,
  ]);

  Object.assign(
    potentialRevenueSettings,
    potRevSettingsStore.potentialRevenueSettings,
  );
});

function addSetting(setting: PopulationType, org: OrgType) {
  potentialRevenueSettings[org]?.push(setting);
}

function isChecked(setting: PopulationType, org: OrgType) {
  return potentialRevenueSettings[org]?.includes(setting);
}

async function onSaveSettings() {
  isSaving.value = true;
  try {
    await crossbeamApi.POST('/v0.1/organization/settings/potential-influence', {
      body: {
        our_populations_potential_influence:
          potentialRevenueSettings.our_populations_potential_influence,
        partner_populations_potential_influence:
          potentialRevenueSettings.partner_populations_potential_influence,
      },
    });
    await potRevSettingsStore.refreshPotentialRevenueStore();
    addSuccessFlash({
      message: 'Potential revenue settings saved',
      description: 'It will take some time for these changes to be reflected.',
    });
    haveSettingsChanged.value = false;
  } catch (err) {
    captureException(err);
    addErrorFlash({
      message: 'Could not save field mapping settings',
      description: `If this error persists, please contact
          <a target="_blank" href="mailto:support@crossbeam.com">support@crossbeam.com</a>.`,
    });
  } finally {
    isSaving.value = false;
  }
}

function handleItlySyncData(dataType = 'sync_crm') {
  iteratively.userClickedSyncData({
    cta_location: 'Potential Revenue Configuration', // TODO: Deprecate, this is EVENT_SITE now
    event_site: EVENT_SITES.POTENTIAL_REVENUE_CONFIGURATION,
    data_type: dataType,
  });
}

function onSettingSelected(setting: PopulationType, org: OrgType) {
  if (!hasOrgSettingsPermission.value) return;
  haveSettingsChanged.value = true;
  if (isChecked(setting, org)) removeSetting(setting, org);
  else addSetting(setting, org);
}

async function handleConnectDataSource() {
  handleItlySyncData();
  await router.push({ name: 'data-sources' });
}

function handleDismiss() {
  calloutDismissed.value = true;
}

function removeSetting(setting: PopulationType, org: OrgType) {
  potentialRevenueSettings[org] = potentialRevenueSettings[org]?.filter(
    (potRevSetting) => potRevSetting !== setting,
  );
}
</script>

<style scoped lang="pcss">
@reference "../css/theme.css";
.potential-revenue-card__checkbox-group {
  @apply border-neutral-border shadow-component w-full;
}
.potential-revenue-card__settings {
  @apply flex items-center my-24;
}
.potential-revenue-card__text {
  @apply text-neutral-text max-w-[680px] mb-16 text-sm;
}

.potential-revenue-card h3 {
  @apply text-neutral-text-strong font-bold mb-8 block;
}
</style>

<style lang="pcss">
@reference "../css/theme.css";
.potential-revenue-card {
  .bitts-card__title {
    @apply text-lg text-neutral-text-strong font-bold;
  }
}
</style>
